import { JSONContent } from "@tiptap/core";
import { uniqueId } from "lodash";
import {
  FinalizePermissions,
  NotesRequirement,
  TopicTemplateFragmentFragment,
} from "types/graphql-schema";

import { SelectOption } from "@components/select/select";

export type EditTemplateTopicType = {
  id: number | string;
  isMandatory: boolean;
  notesRequirement: NotesRequirement;
  title: string;
  description: string;
  discussionNotes: string | JSONContent;
  defaultSubjectNotes: string | JSONContent;
  defaultFacilitatorNotes: string | JSONContent;
  includesIndividualNotes: boolean;
  includesSharedNotes: boolean;
};

export const getNewTopic = (): EditTemplateTopicType => {
  const id = uniqueId("topic-");
  const newTopic = {
    id,
    isMandatory: false,
    notesRequirement: NotesRequirement.NotRequired,
    title: "",
    description: "",
    discussionNotes: "",
    defaultSubjectNotes: "",
    defaultFacilitatorNotes: "",
    includesIndividualNotes: false,
    includesSharedNotes: true,
  };
  return newTopic;
};

export const emptyTemplate: Partial<TopicTemplateFragmentFragment> = {
  publicTemplate: true,
  title: "",
  oneononeTemplate: true,
  finalizePermissions: FinalizePermissions.FacilitatorsAndAdmins,
  description: "",
  categoriesList: [],
  managerInstructions: "",
  reportInstructions: "",
  allowAdminVisibility: true,
  allowManagementTreeVisibility: true,
  allowOrgVisibility: false,
};

export const removeIdFromTopics = (topics: any) =>
  topics.map((node: any) => ({
    id: `topic-${node.id}`,
    title: node.title,
    description: node.description,
    isMandatory: node.isMandatory,
    discussionNotes: node.discussionNotes,
    defaultSubjectNotes: node.defaultSubjectNotes,
    defaultFacilitatorNotes: node.defaultFacilitatorNotes,
    includesIndividualNotes: node.includesIndividualNotes,
  })) || [];

export const isSameObject = (obj1: unknown, obj2: unknown) =>
  JSON.stringify(obj1) === JSON.stringify(obj2);

export const removeTopicsWithoutTitles = (topics: any[]) =>
  topics.filter((topic) => {
    return topic.title.length > 0;
  });

type TopicRequirementOption = SelectOption<number> & {
  isMandatory: boolean;
  notesRequirement: NotesRequirement;
};

export const topicRequirementsOptions = [
  {
    value: 1,
    label: "Not mandatory",
    description: "Topic title can be edited.",
    isMandatory: false,
    notesRequirement: NotesRequirement.NotRequired,
  },
  {
    value: 2,
    label: "Mandatory, notes optional",
    description:
      "Topic title cannot be edited. No notes are required to finalize.",
    isMandatory: true,
    notesRequirement: NotesRequirement.NotRequired,
  },
  {
    value: 3,
    label: "Mandatory, notes required by one participant",
    description:
      "Topic title cannot be edited. Notes must be entered by one participant to finalize.",
    isMandatory: true,
    notesRequirement: NotesRequirement.OneParticipant,
  },
  {
    value: 4,
    label: "Mandatory, notes required by all participants",
    description:
      "Topic title cannot be edited. Notes must be entered by all participants to finalize.",
    isMandatory: true,
    notesRequirement: NotesRequirement.AllParticipants,
  },
] as TopicRequirementOption[];
